import * as version from '#base/parsedVersion.js';
import opentype from 'opentype.js';

type FontFormatFn = (input: string, decoration: string | undefined) => string;

type FontConfig = {
  name: string;
  minLength?: number;
  maxLength?: number;
  decorations?: string[];
  ampersand?: boolean;
  format?: FontFormatFn;
};

export const FONT_RENDERING_SIZE = 150;
export const MonogramFontConfig: FontConfig[] = [
  {
    name: 'Hex',
    minLength: 3,
    maxLength: 3,
    decorations: ['-', ',', '0', '1', '2', '3', '4', '5', '9', '6', '7', ':', ';', '<', '=', '8'],
    ampersand: true,
    format: (input, decoration) => {
      //console.log({ input, decoration })
      const output = String.fromCharCode(
        ...Array.from(input.trim().toLowerCase()).map((c, i) => {
          const code = c.charCodeAt(0);
          const mod = i % 3;
          //console.log({ code, mod, c})
          if (mod === 2) {
            return code + 57344 - 97;
          } else if (mod === 1) {
            return code - 32;
          } else {
            return code;
          }
        })
      );
      return (decoration ? decoration : '') + output;
    },
  },
  {
    name: 'Hex Ornate',
    minLength: 3,
    maxLength: 3,
    decorations: ['-', ',', '0', '1', '2', '3', '4', '5', '9', '6', '7', ':', ';', '<', '=', '8'],
    ampersand: true,
    format: (input, decoration) => {
      //console.log({ input, decoration })
      const output = String.fromCharCode(
        ...Array.from(input.trim().toLowerCase()).map((c, i) => {
          const code = c.charCodeAt(0);
          const mod = i % 3;
          //console.log({ code, mod, c})
          if (mod === 2) {
            return code + 57344 - 97;
          } else if (mod === 1) {
            return code - 32;
          } else {
            return code;
          }
        })
      );
      return (decoration ? decoration : '') + output;
    },
  },
  {
    name: 'Flotta',
    decorations: [
      '{}',
      '[]',
      '\\',
      //'\\\\',
      '|',
      //'||',
    ],
    format: (input, decoration) => {
      if (decoration === '{}') {
        return '{' + input + '}';
      } else if (decoration === '[]') {
        return '[' + input + ']';
      } else if (decoration === '\\') {
        return input + '\n\\';
      } else if (decoration === '\\\\') {
        return '|\n' + input + '\n\\';
      } else if (decoration === '|') {
        return input + '\n|';
      } else if (decoration === '||') {
        return '\\\n' + input + '\n|';
      } else {
        return input;
      }
    },
  },
];

export type RenderedPath = {
  data: string;
  width: number;
  height: number;
};

export type PathRenderOptions = {
  elementType?: string;
  version?: string;
  decoration?: string;
  align?: 'left' | 'center' | 'right';
};

export type FontDef = FontConfig & {
  file: string | ArrayBuffer;
};

export class Font {
  key: string;
  def: FontDef;
  draw?: opentype.Font;
  loaded: boolean = false;

  constructor(key: string, def: FontDef) {
    this.key = key;
    this.def = def;
    this.loaded = false;
  }

  load(onLoad: (f: Font) => void) {
    if (this.loaded) {
      onLoad && onLoad(this);
      return;
    }

    if (typeof this.def.file === 'string') {
      // url (for browser)
      opentype.load(this.def.file, (err, loaded) => {
        err && console.error('Error loading font', err);
        // console.log('loaded', this.key, loaded);
        if (!err) {
          this.draw = loaded;
          this.loaded = true;
          onLoad && onLoad(this);
        }
      });
    } else {
      // pre-downloaded
      const f = opentype.parse(this.def.file);
      if (f) {
        this.draw = f;
        this.loaded = true;
        onLoad && onLoad(this);
      } else {
        console.log(`[error] could not load font (name ${this.def.name})`);
      }
    }
  }

  format(input: string, options: PathRenderOptions | undefined) {
    if (options?.elementType === 'title') {
      return [input, options?.decoration].filter((v) => !!v).join(' | ');
    } else {
      return input || '';
    }
  }

  pathWithBounds(input: string, options: PathRenderOptions | undefined): RenderedPath | undefined {
    if (this.loaded) {
      if (options?.elementType === 'title') {
        return this._renderTitle(input, options);
      } else {
        return this._renderMultiline(input, options);
      }
    } else {
      console.debug(this.key, 'cannot build path, font is not loaded');
      return;
    }
  }

  path(input: string, options: PathRenderOptions | undefined) {
    return this.pathWithBounds(input, options)?.data;
  }

  _renderMultiline(input: string, options: PathRenderOptions | undefined): RenderedPath | undefined {
    if (!this.draw) {
      console.debug(this.key, 'cannot build path, font is not loaded');
      return;
    }

    const { align = 'left' } = options || {};

    const fontSize = FONT_RENDERING_SIZE;
    const text = this.format(input, options);
    const split = text.split(/[\r\n]+/g);

    const { unitsPerEm, ascender, descender } = this.draw;
    const scale = (1 / unitsPerEm) * fontSize;

    const bounds = split.map((t) => {
      const path = this.draw!.getPath(t, 0, 0, fontSize);
      const bb = path.getBoundingBox();
      const { x1, x2, y1, y2 } = bb;
      // Font not infrequently lie about their bounds. In fact, our cursive font, Gwendolyn-Bold
      // exhibits this behavior with the capital Y.
      const above = Math.max(ascender * scale, -y1);
      const below = Math.max(-descender * scale, y2);
      return { w: x2 - x1, h: above + below, bb };
    });

    const maxWidth = Math.max(...bounds.map(({ w }) => w));

    const useDescender = version.gte(options?.version, '1.0.191');

    const data = split
      .map((t, i) => {
        const {
          w,
          bb: { x1 },
        } = bounds[i];
        const x = (align === 'center' ? (maxWidth - w) / 2 : align === 'right' ? maxWidth - w : 0) - x1;
        const y = ascender * scale * (i + 1) - (useDescender ? descender * scale * i : 0);
        const path = this.draw!.getPath(t, x, y, fontSize);

        return path.toPathData(3);
      })
      .join(' ');

    const fontHeight = (ascender - descender) * scale;
    const textHeight = useDescender
      ? fontHeight * split.length
      : ascender * scale * split.length - descender * scale;
    // Calculate the extra we need if the font lied about its bounds
    const extra = Math.max(0.0, (bounds[0].h || fontHeight) - fontHeight);

    return { data, width: maxWidth, height: textHeight + extra };
  }

  _renderTitle(text: string, { decoration = '' }): RenderedPath | undefined {
    if (!this.draw) {
      console.debug(this.key, 'cannot build path, font is not loaded');
      return;
    }

    const lineWidth = 3;
    const fontSize = FONT_RENDERING_SIZE;
    const decorationSize = fontSize * 0.5;

    const { unitsPerEm, ascender, descender } = this.draw;
    const scale = (1 / unitsPerEm) * fontSize;
    const decorationScale = (1 / unitsPerEm) * decorationSize;

    const textY = ascender * scale;
    const textPath = this.draw.getPath(text, 0, textY, fontSize);
    const textBounds = textPath.getBoundingBox();
    const textWidth = textBounds.x2 - textBounds.x1;

    const decorationY = (ascender - descender) * scale + ascender * decorationScale;
    const decorationPath = this.draw.getPath(decoration, 0, decorationY, decorationSize);
    const decorationBounds = decorationPath.getBoundingBox();
    const decorationWidth = decorationBounds.x2 - decorationBounds.x1;

    const maxWidth = Math.max(textWidth, decorationWidth);

    const textPathOffset = this.draw.getPath(
      text,
      (maxWidth - textWidth) / 2 - textBounds.x1,
      textY,
      fontSize
    );
    const decorationPathOffset = this.draw.getPath(
      decoration,
      (maxWidth - decorationWidth) / 2 - decorationBounds.x1,
      decorationY,
      decorationSize
    );

    const height = decorationY - descender * decorationScale;

    const lineY = textY - (descender / 2) * scale;
    const linePath = `M0 ${lineY}h${maxWidth}v${lineWidth}H0Z`;

    const data = [textPathOffset, decorationPathOffset]
      .map((p) => p.toPathData(3))
      .concat(linePath)
      .join('');

    return { data, width: maxWidth, height };
  }
}

export class MonogramFont extends Font {
  format(input: string, options: PathRenderOptions | undefined) {
    return this.def?.format ? this.def.format(input, options?.decoration) : input;
  }

  /*
    path(input, decoration){
        if (this.loaded) {
            const fontSize = FONT_RENDERING_SIZE;
            const text = this.format(input, decoration);
            const split = text.split(/__/g);

            const bounds = split.map(t => {
                const path = this.draw.getPath(t,0,0,fontSize);
                const { x1, x2, y1, y2 } = path.getBoundingBox();
                return [x2 - x1, y2 - y1];
            });
            const maxWidth = bounds.reduce((a,[w]) => a < w ? w : a, 0);

            let y = 0;
            const data = split.map((t,i) => {
                const [w,h] = bounds[i];
                const x = (maxWidth - w) / 2;
                const path = this.draw.getPath(t,x,y,fontSize);
                y += h;
                return path.toPathData(3);
            }).join(' ');

            // const data = this.draw.getPath(text,0,0,FONT_RENDERING_SIZE).toPathData(3);
            return data;
        } else {
            return null;
        }
    }
    */
}

export type FontFamilyFonts = {
  left: Font;
  center: Font;
  right: Font;
};

export class MonogramFontFamily {
  key: string;
  fonts: FontFamilyFonts;
  draw?: opentype.Font;
  loaded: boolean;

  constructor(
    key: string,
    fonts: {
      left: Font;
      center: Font;
      right: Font;
    }
  ) {
    this.key = key;
    this.fonts = fonts;
    this.loaded = false;
  }

  load(onLoad: (font: MonogramFontFamily) => void) {
    if (this.loaded) {
      onLoad && onLoad(this);
      return;
    }

    Object.values(this.fonts).forEach((font) => {
      font.load((_f) => {
        this.loaded = Object.values(this.fonts).every((f) => f.loaded);
        this.loaded && onLoad && onLoad(this);
      });
    });
  }

  format(input: string, _options: PathRenderOptions | undefined) {
    return (input || '').toUpperCase();
  }

  pathWithBounds(input: string, options: PathRenderOptions | undefined): RenderedPath | undefined {
    if (this.loaded) {
      const fontSize = FONT_RENDERING_SIZE;
      const text = this.format(input, options);
      const letters = Array.from(text.trim()).slice(0, 3);

      const fonts: opentype.Font[] = (
        letters.length === 3
          ? [this.fonts.left.draw, this.fonts.center.draw, this.fonts.right.draw]
          : letters.length === 2
            ? [this.fonts.left.draw, this.fonts.right.draw]
            : [this.fonts.center.draw]
      ).filter((f): f is opentype.Font => !!f);

      if (fonts.length != letters.length) {
        return;
      }

      // TODO its _possible_ for fonts in the same family to have different ascender/descenders
      // but respecting them so far leads to visual bugs...
      const { unitsPerEm } = fonts[0];
      const ascender = Math.max(...fonts.map((f) => f.ascender));
      const descender = Math.min(...fonts.map((f) => f.descender));
      const scale = (1 / unitsPerEm) * fontSize;

      const paths = letters.map((l, i) => {
        const draw = fonts[i];

        const offset =
          i > 0
            ? fonts.slice(0, i).reduce((a, f, i) => (a += f.getAdvanceWidth(letters[i], fontSize)), 0)
            : 0;
        const path = draw.getPath(l, offset, ascender * scale, fontSize);
        const advance = draw.getAdvanceWidth(l, fontSize);
        /*
        const kerning = letters[i-1] && draw.getKerningValue(letters[i-1], l);
        console.log({ kerning });
        */

        return { path, advance };
      });

      const data = paths.map(({ path }) => path.toPathData(3)).join(' ');
      const pathBounds = paths.map(({ path }) => path.getBoundingBox());

      const fontHeight = (ascender - descender) * scale;

      const fontWidth = paths.reduce((a, { advance }, i) => {
        if (i < paths.length - 1) {
          a += advance;
        } else {
          const { x1, x2 } = pathBounds[i];
          a += Math.max(advance, x2 - x1);
        }
        return a;
      }, 0);

      const height = Math.max(...pathBounds.map((p) => p.y2)) - Math.max(...pathBounds.map((p) => -p.y1));
      const extraH = Math.max(0.0, height - fontHeight);

      const width = Math.max(...pathBounds.map((p) => p.x2)) - Math.max(...pathBounds.map((p) => -p.x1));
      const extraW = Math.max(0.0, width - fontWidth);

      return { data, width: fontWidth + extraW, height: fontHeight + extraH };
    } else {
      console.debug(this.key, 'cannot build path, font is not loaded');
      return;
    }
  }

  path(input: string, options: PathRenderOptions | undefined) {
    return this.pathWithBounds(input, options)?.data;
  }
}

export class JerseyFont extends Font {
  format(input: string, options: PathRenderOptions | undefined) {
    return [input, options?.decoration].filter((s) => !!s).join('\n');
  }

  pathWithBounds(text: string, { decoration = '' } = {}): RenderedPath | undefined {
    if (this.loaded && this.draw) {
      // TODO
      // max name characters (none?)
      // max number characters {1,3}
      // default pad from max size (80-90%, whatever looks good, actually max for now)
      // determine max space for numbers, name fits to remaining space
      // (intention is to keep number placement consistent regardless of name length)

      const fontSize = FONT_RENDERING_SIZE;
      const margin = fontSize / 30; // space between name and number
      const maxScale = 0.3; // max height ratio of name to numbers

      const { unitsPerEm, ascender, descender } = this.draw;
      const scale = (1 / unitsPerEm) * fontSize;

      // Note that we compute the width from the actual path of the glyphs, not the glyph rects.
      // This means that path.x1 will not be 0 (since the glyph often does not actually start at
      // the left of the glyph rect). But since our scaling is based on the path width, we need
      // offset the starting x value by path.x1 so that the scaled path starts at 0. Otherwise
      // the right side may get clipped.

      const numberPath = this.draw.getPath(decoration, 0, 0, fontSize);
      const numberBounds = numberPath.getBoundingBox();
      const numberWidth = numberBounds.x2 - numberBounds.x1;

      const textPath = this.draw.getPath(text, 0, 0, fontSize);
      const textBounds = textPath.getBoundingBox();
      const textWidth = textBounds.x2 - textBounds.x1;

      const textScaleRaw = numberWidth / textWidth;
      const textScale = textScaleRaw > maxScale ? maxScale : textScaleRaw;

      const textY = ascender * scale * textScale;
      const textX = (numberWidth - textWidth * textScale) / 2;
      const textScaled = this.draw.getPath(
        text,
        textX - textBounds.x1 * textScale,
        textY,
        fontSize * textScale
      );
      const textScaledHeight = (ascender - descender) * scale * textScale;

      const numberY = textScaledHeight + margin + ascender * scale;
      const numberX = -numberBounds.x1;
      const numberOffset = this.draw.getPath(decoration, numberX, numberY, fontSize);
      const numberOffsetBounds = numberOffset.getBoundingBox();

      const height = numberOffsetBounds.y2;

      const data = [textScaled, numberOffset].map((p) => p.toPathData(3)).join(' ');

      return { data, width: numberWidth, height };
    } else {
      console.debug(this.key, 'cannot build path, font is not loaded');
      return;
    }
  }
}
